import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { type KeyboardEvent, useId, useState } from "react";
import { formatArticle } from "@/content/format";
import { productsQuery } from "@/content/queries";
import type { ProductSummary } from "@/content/schema";
import { useThrottledValue } from "@/hooks/useThrottledValue";
import { fr } from "@/i18n/fr";
import { StatusBadge } from "./StatusBadge";

interface ProductSearchProps {
	/** Number of suggestions shown. */
	limit?: number;
}

/** Search a product by name and jump to it (combobox: ↑ ↓ to move, Enter to open, Esc to close). */
export function ProductSearch({ limit = 8 }: ProductSearchProps) {
	const navigate = useNavigate();
	const listId = useId();
	const [input, setInput] = useState("");
	const [open, setOpen] = useState(false);
	const [active, setActive] = useState(0);
	const q = useThrottledValue(input.trim());
	const { data, isFetching } = useQuery({
		...productsQuery(q),
		enabled: q.length > 0,
	});
	const items = q ? (data?.items.slice(0, limit) ?? []) : [];
	const expanded = open && q.length > 0;
	const current = items[Math.min(active, items.length - 1)];

	const choose = (product: ProductSummary) => {
		setOpen(false);
		setInput("");
		navigate({ to: "/products/$slug", params: { slug: product.slug } });
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			e.preventDefault();
			setOpen(true);
			const step = e.key === "ArrowDown" ? 1 : -1;
			setActive((i) =>
				items.length ? (i + step + items.length) % items.length : 0,
			);
		} else if (e.key === "Enter" && expanded && current) {
			e.preventDefault();
			choose(current);
		} else if (e.key === "Escape") {
			setOpen(false);
		}
	};

	return (
		<div className="relative w-36 min-w-0 sm:w-56 md:w-72">
			<Search
				aria-hidden="true"
				className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
			/>
			<input
				type="search"
				role="combobox"
				aria-label={fr.searchLabel}
				aria-autocomplete="list"
				aria-expanded={expanded}
				aria-controls={listId}
				aria-activedescendant={
					expanded && current ? `${listId}-${current.slug}` : undefined
				}
				placeholder={fr.searchPlaceholder}
				value={input}
				onChange={(e) => {
					setInput(e.target.value);
					setActive(0);
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				onBlur={() => setOpen(false)}
				onKeyDown={onKeyDown}
				className="h-9 w-full rounded-md border border-line-strong bg-bg-elev pr-3 pl-9 text-fg text-sm outline-none transition placeholder:text-fg-muted focus:border-accent focus:ring-[3px] focus:ring-accent-soft"
			/>
			{expanded && (
				<div
					id={listId}
					role="listbox"
					aria-label={fr.searchLabel}
					className="absolute top-full right-0 z-20 mt-1 max-h-96 w-[min(24rem,calc(100vw-1.5rem))] overflow-y-auto rounded-lg border border-line bg-bg-elev p-1 shadow-md"
				>
					{items.map((product) => (
						<div
							key={product.slug}
							id={`${listId}-${product.slug}`}
							role="option"
							tabIndex={-1}
							aria-selected={product === current}
							// mousedown: select before the input blur closes the list
							onMouseDown={(e) => {
								e.preventDefault();
								choose(product);
							}}
							onMouseEnter={() => setActive(items.indexOf(product))}
							className={`flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2 ${product === current ? "bg-accent-soft" : ""}`}
						>
							<span className="min-w-0">
								<span className="block truncate font-semibold">
									{product.name}
								</span>
								<span className="block truncate text-[13px] text-fg-muted">
									{product.designation} · {formatArticle(product.ikea_id)}
								</span>
							</span>
							<StatusBadge status={product.generation_status} />
						</div>
					))}
					{items.length === 0 && (
						<p className="px-3 py-2 text-fg-muted">
							{isFetching || q !== input.trim() ? fr.searching : fr.noResults}
						</p>
					)}
				</div>
			)}
		</div>
	);
}
